//src/pages/personnel/PersonnelPublicHolidaysPage.jsx
import React, {
  useMemo,
  useState,
} from "react";

import {
  LuCalendarCheck2,
  LuCalendarRange,
  LuChevronLeft,
  LuChevronRight,
  LuCircleDollarSign,
  LuGlobe,
  LuMapPin,
  LuPencil,
  LuPlus,
  LuRefreshCw,
  LuRepeat2,
  LuSearch,
} from "react-icons/lu";

import {
  useTranslation,
} from "react-i18next";

import usePersonnelPublicHolidaysData from "../../hooks/usePersonnelPublicHolidaysData";
import usePersonnelPermissions from "../../hooks/usePersonnelPermissions";

import PersonnelPublicHolidayFormModal from "../../components/personnel/PersonnelPublicHolidayFormModal";

/* =========================================================
 * HELPERS
 * ======================================================= */

const normalizeText =
  (
    value
  ) =>
    String(
      value ||
        ""
    )
      .trim()
      .toLocaleLowerCase();

const parseDateOnly =
  (
    value
  ) => {
    const [
      year,
      month,
      day,
    ] =
      String(
        value ||
          ""
      )
        .split("-")
        .map(Number);

    return new Date(
      year,
      month - 1,
      day
    );
  };

/* =========================================================
 * PAGE
 * ======================================================= */

export default function PersonnelPublicHolidaysPage() {
  const {
    t,
    i18n,
  } =
    useTranslation(
      "personnel"
    );

  const [
    year,
    setYear,
  ] =
    useState(
      () =>
        new Date()
          .getFullYear()
    );

  const [
    search,
    setSearch,
  ] =
    useState(
      ""
    );

  const [
    countryFilter,
    setCountryFilter,
  ] =
    useState(
      "ALL"
    );

  const [
    recurringFilter,
    setRecurringFilter,
  ] =
    useState(
      "ALL"
    );

  const [
    selectedHoliday,
    setSelectedHoliday,
  ] =
    useState(
      null
    );

  const [
    holidayEditor,
    setHolidayEditor,
  ] =
    useState(
      null
    );

  const {
    publicHolidays,
    loading,
    refreshing,
    error,
    refresh,
  } =
    usePersonnelPublicHolidaysData(
      year
    );

  const {
    publicHolidaysCreate,
    publicHolidaysEdit,
    publicHolidaysDelete,
  } =
    usePersonnelPermissions();

  const locale =
    String(
      i18n.resolvedLanguage ||
        i18n.language ||
        "en"
    );

  /* =======================================================
   * COUNTRIES
   * ===================================================== */

  const countries =
    useMemo(
      () =>
        Array.from(
          new Set(
            publicHolidays
              .map(
                (
                  holiday
                ) =>
                  String(
                    holiday.countryCode ||
                      ""
                  )
                    .trim()
                    .toUpperCase()
              )
              .filter(
                Boolean
              )
          )
        ).sort(),
      [
        publicHolidays,
      ]
    );

  /* =======================================================
   * FILTERED ROWS
   * ===================================================== */

  const filteredHolidays =
    useMemo(
      () => {
        const needle =
          normalizeText(
            search
          );

        return publicHolidays
          .filter(
            (
              holiday
            ) => {
              if (
                countryFilter !==
                  "ALL" &&
                String(
                  holiday.countryCode ||
                    ""
                ).toUpperCase() !==
                  countryFilter
              ) {
                return false;
              }

              if (
                recurringFilter ===
                  "RECURRING" &&
                !holiday.isRecurring
              ) {
                return false;
              }

              if (
                recurringFilter ===
                  "ONE_TIME" &&
                holiday.isRecurring
              ) {
                return false;
              }

              if (!needle) {
                return true;
              }

              const haystack =
                [
                  holiday.name,
                  holiday.countryCode,
                  holiday.regionCode,
                  holiday.notes,
                ]
                  .map(
                    normalizeText
                  )
                  .join(
                    " "
                  );

              return haystack.includes(
                needle
              );
            }
          )
          .sort(
            (
              first,
              second
            ) =>
              String(
                first.date
              ).localeCompare(
                String(
                  second.date
                )
              )
          );
      },
      [
        publicHolidays,
        search,
        countryFilter,
        recurringFilter,
      ]
    );

  /* =======================================================
   * STATS
   * ===================================================== */

  const stats =
    useMemo(
      () => ({
        total:
          publicHolidays.length,

        paid:
          publicHolidays.filter(
            (
              holiday
            ) =>
              holiday.isPaidHoliday
          ).length,

        regional:
          publicHolidays.filter(
            (
              holiday
            ) =>
              Boolean(
                String(
                  holiday.regionCode ||
                    ""
                ).trim()
              )
          ).length,

        recurring:
          publicHolidays.filter(
            (
              holiday
            ) =>
              holiday.isRecurring
          ).length,
      }),
      [
        publicHolidays,
      ]
    );

  /* =======================================================
   * FORMAT
   * ===================================================== */

  const formatDate =
    (
      value
    ) =>
      new Intl.DateTimeFormat(
        locale,
        {
          day:
            "2-digit",

          month:
            "short",

          year:
            "numeric",
        }
      ).format(
        parseDateOnly(
          value
        )
      );

  const formatLongDate =
    (
      value
    ) =>
      new Intl.DateTimeFormat(
        locale,
        {
          dateStyle:
            "long",
        }
      ).format(
        parseDateOnly(
          value
        )
      );

  /* =======================================================
   * YEAR
   * ===================================================== */

  const previousYear =
    () => {
      setYear(
        (
          current
        ) =>
          current - 1
      );

      setSelectedHoliday(
        null
      );
    };

  const nextYear =
    () => {
      setYear(
        (
          current
        ) =>
          current + 1
      );

      setSelectedHoliday(
        null
      );
    };

  const currentYear =
    () => {
      setYear(
        new Date()
          .getFullYear()
      );

      setSelectedHoliday(
        null
      );
    };

  /* =======================================================
   * CREATE / EDIT
   * ===================================================== */

  const openCreateHoliday =
    () => {
      setSelectedHoliday(
        null
      );

      setHolidayEditor({
        mode:
          "create",

        holiday:
          null,
      });
    };

  const openEditHoliday =
    (
      holiday
    ) => {
      setSelectedHoliday(
        null
      );

      setHolidayEditor({
        mode:
          "edit",

        holiday,
      });
    };

  const closeHolidayEditor =
    () => {
      setHolidayEditor(
        null
      );
    };

  const handleHolidayChanged =
    async () => {
      await refresh({
        silent:
          true,
      });
    };

  /* =======================================================
   * RENDER
   * ===================================================== */

  return (
    <section className="personnel-page personnel-holidays-page">
      <div className="personnel-holidays-stats">
        <div className="personnel-holiday-stat">
          <LuCalendarRange />

          <div>
            <strong>
              {
                stats.total
              }
            </strong>

            <span>
              {t(
                "public_holidays.stats.total"
              )}
            </span>
          </div>
        </div>

        <div className="personnel-holiday-stat is-paid">
          <LuCircleDollarSign />

          <div>
            <strong>
              {
                stats.paid
              }
            </strong>

            <span>
              {t(
                "public_holidays.stats.paid"
              )}
            </span>
          </div>
        </div>

        <div className="personnel-holiday-stat is-regional">
          <LuMapPin />

          <div>
            <strong>
              {
                stats.regional
              }
            </strong>

            <span>
              {t(
                "public_holidays.stats.regional"
              )}
            </span>
          </div>
        </div>

        <div className="personnel-holiday-stat is-recurring">
          <LuRepeat2 />

          <div>
            <strong>
              {
                stats.recurring
              }
            </strong>

            <span>
              {t(
                "public_holidays.stats.recurring"
              )}
            </span>
          </div>
        </div>
      </div>

      <div className="personnel-holidays-toolbar">
        <div className="personnel-holidays-year">
          <button
            type="button"
            onClick={
              previousYear
            }
            aria-label={t(
              "public_holidays.previous_year"
            )}
          >
            <LuChevronLeft />
          </button>

          <button
            type="button"
            className="personnel-holidays-current-year"
            onClick={
              currentYear
            }
          >
            {t(
              "public_holidays.current_year"
            )}
          </button>

          <button
            type="button"
            onClick={
              nextYear
            }
            aria-label={t(
              "public_holidays.next_year"
            )}
          >
            <LuChevronRight />
          </button>

          <strong>
            {
              year
            }
          </strong>
        </div>

        <div className="personnel-holidays-toolbar-right">
          <div className="personnel-holidays-search">
            <LuSearch />

            <input
              type="text"
              value={
                search
              }
              onChange={(
                event
              ) =>
                setSearch(
                  event.target
                    .value
                )
              }
              placeholder={t(
                "public_holidays.search_placeholder"
              )}
            />
          </div>

          <select
            value={
              countryFilter
            }
            onChange={(
              event
            ) =>
              setCountryFilter(
                event.target
                  .value
              )
            }
          >
            <option value="ALL">
              {t(
                "public_holidays.filters.all_countries"
              )}
            </option>

            {countries.map(
              (
                country
              ) => (
                <option
                  key={
                    country
                  }
                  value={
                    country
                  }
                >
                  {
                    country
                  }
                </option>
              )
            )}
          </select>

          <select
            value={
              recurringFilter
            }
            onChange={(
              event
            ) =>
              setRecurringFilter(
                event.target
                  .value
              )
            }
          >
            <option value="ALL">
              {t(
                "public_holidays.filters.all_recurrence"
              )}
            </option>

            <option value="RECURRING">
              {t(
                "public_holidays.filters.recurring"
              )}
            </option>

            <option value="ONE_TIME">
              {t(
                "public_holidays.filters.one_time"
              )}
            </option>
          </select>

          <div className="personnel-toolbar-actions">
            {publicHolidaysCreate && (
              <button
                type="button"
                className="personnel-new-holiday-btn"
                onClick={
                  openCreateHoliday
                }
              >
                <LuPlus />

                <span>
                  {t(
                    "public_holidays.form.new_holiday"
                  )}
                </span>
              </button>
            )}

            <button
              type="button"
              className="personnel-refresh-btn"
              onClick={() =>
                refresh({
                  silent:
                    true,
                })
              }
              disabled={
                refreshing
              }
            >
              <LuRefreshCw
                className={
                  refreshing
                    ? "is-spinning"
                    : ""
                }
              />

              <span>
                {t(
                  "public_holidays.refresh"
                )}
              </span>
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="personnel-calendar-warning">
          {t(
            "public_holidays.refresh_warning"
          )}
        </div>
      )}

      <div className="personnel-holidays-table-card">
        <div className="personnel-holidays-table-header">
          <div>
            {t(
              "public_holidays.columns.date"
            )}
          </div>

          <div>
            {t(
              "public_holidays.columns.name"
            )}
          </div>

          <div>
            {t(
              "public_holidays.columns.country"
            )}
          </div>

          <div>
            {t(
              "public_holidays.columns.region"
            )}
          </div>

          <div>
            {t(
              "public_holidays.columns.recurrence"
            )}
          </div>

          <div>
            {t(
              "public_holidays.columns.payment"
            )}
          </div>
        </div>

        {loading &&
        publicHolidays.length ===
          0 ? (
          Array.from({
            length: 4,
          }).map(
            (
              _,
              index
            ) => (
              <div
                className="personnel-holiday-row personnel-holiday-row--skeleton"
                key={
                  index
                }
              >
                {Array.from({
                  length: 6,
                }).map(
                  (
                    __,
                    cellIndex
                  ) => (
                    <div
                      key={
                        cellIndex
                      }
                    >
                      <span className="personnel-holiday-skeleton" />
                    </div>
                  )
                )}
              </div>
            )
          )
        ) : filteredHolidays.length ===
          0 ? (
          <div className="personnel-holidays-empty">
            <LuCalendarRange />

            <strong>
              {t(
                "public_holidays.empty.title"
              )}
            </strong>

            <span>
              {t(
                "public_holidays.empty.subtitle"
              )}
            </span>
          </div>
        ) : (
          filteredHolidays.map(
            (
              holiday
            ) => (
              <button
                type="button"
                className="personnel-holiday-row"
                key={
                  holiday.id
                }
                onClick={() =>
                  setSelectedHoliday(
                    holiday
                  )
                }
              >
                <div className="personnel-holiday-date">
                  <LuCalendarCheck2 />

                  <strong>
                    {formatDate(
                      holiday.date
                    )}
                  </strong>
                </div>

                <div className="personnel-holiday-name">
                  <strong>
                    {
                      holiday.name
                    }
                  </strong>

                  {holiday.notes && (
                    <span>
                      {
                        holiday.notes
                      }
                    </span>
                  )}
                </div>

                <div className="personnel-holiday-country">
                  <LuGlobe />

                  <span>
                    {holiday.countryCode ||
                      "—"}
                  </span>
                </div>

                <div>
                  {holiday.regionCode ||
                    "—"}
                </div>

                <div>
                  <span
                    className={[
                      "personnel-holiday-badge",

                      holiday.isRecurring
                        ? "is-recurring"
                        : "is-one-time",
                    ].join(
                      " "
                    )}
                  >
                    {holiday.isRecurring
                      ? t(
                          "public_holidays.values.recurring"
                        )
                      : t(
                          "public_holidays.values.one_time"
                        )}
                  </span>
                </div>

                <div>
                  <span
                    className={[
                      "personnel-holiday-badge",

                      holiday.isPaidHoliday
                        ? "is-paid"
                        : "is-unpaid",
                    ].join(
                      " "
                    )}
                  >
                    {holiday.isPaidHoliday
                      ? t(
                          "public_holidays.values.paid"
                        )
                      : t(
                          "public_holidays.values.unpaid"
                        )}
                  </span>
                </div>
              </button>
            )
          )
        )}
      </div>

      <div className="personnel-holidays-footer">
        {t(
          "public_holidays.showing",
          {
            shown:
              filteredHolidays.length,

            total:
              publicHolidays.length,

            year,
          }
        )}
      </div>

      {selectedHoliday && (
        <div
          className="personnel-holiday-overlay"
          onMouseDown={(
            event
          ) => {
            if (
              event.target ===
              event.currentTarget
            ) {
              setSelectedHoliday(
                null
              );
            }
          }}
        >
          <div
            className="personnel-holiday-modal"
            role="dialog"
            aria-modal="true"
          >
            <div className="personnel-holiday-modal-header">
              <div>
                <span>
                  {t(
                    "public_holidays.details.kicker"
                  )}
                </span>

                <h3>
                  {
                    selectedHoliday.name
                  }
                </h3>
              </div>

              <button
                type="button"
                onClick={() =>
                  setSelectedHoliday(
                    null
                  )
                }
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="personnel-holiday-modal-details">
              <div>
                <span>
                  {t(
                    "public_holidays.details.date"
                  )}
                </span>

                <strong>
                  {formatLongDate(
                    selectedHoliday.date
                  )}
                </strong>
              </div>

              <div>
                <span>
                  {t(
                    "public_holidays.details.country"
                  )}
                </span>

                <strong>
                  {selectedHoliday.countryCode ||
                    "—"}
                </strong>
              </div>

              <div>
                <span>
                  {t(
                    "public_holidays.details.region"
                  )}
                </span>

                <strong>
                  {selectedHoliday.regionCode ||
                    "—"}
                </strong>
              </div>

              <div>
                <span>
                  {t(
                    "public_holidays.details.recurring"
                  )}
                </span>

                <strong>
                  {selectedHoliday.isRecurring
                    ? t(
                        "common.yes",
                        "Sim"
                      )
                    : t(
                        "common.no",
                        "Não"
                      )}
                </strong>
              </div>

              <div>
                <span>
                  {t(
                    "public_holidays.details.paid"
                  )}
                </span>

                <strong>
                  {selectedHoliday.isPaidHoliday
                    ? t(
                        "common.yes",
                        "Sim"
                      )
                    : t(
                        "common.no",
                        "Não"
                      )}
                </strong>
              </div>

              <div>
                <span>
                  {t(
                    "public_holidays.details.notes"
                  )}
                </span>

                <strong>
                  {selectedHoliday.notes ||
                    t(
                      "public_holidays.details.no_notes"
                    )}
                </strong>
              </div>
            </div>

            <div className="personnel-holiday-modal-actions">
              {publicHolidaysEdit && (
                <button
                  type="button"
                  className="personnel-request-action-btn is-secondary personnel-holiday-edit-btn"
                  onClick={() =>
                    openEditHoliday(
                      selectedHoliday
                    )
                  }
                >
                  <LuPencil />

                  {t(
                    "public_holidays.details.edit"
                  )}
                </button>
              )}

              <button
                type="button"
                className="personnel-request-action-btn is-secondary"
                onClick={() =>
                  setSelectedHoliday(
                    null
                  )
                }
              >
                {t(
                  "public_holidays.details.close"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {holidayEditor &&
        (
          holidayEditor.mode ===
            "create"
            ? publicHolidaysCreate
            : publicHolidaysEdit
        ) && (
        <PersonnelPublicHolidayFormModal
          key={
            holidayEditor
              .holiday
              ?.id ||
            "new-public-holiday"
          }
          year={
            year
          }
          holiday={
            holidayEditor
              .holiday
          }
          canDelete={
            publicHolidaysDelete
          }
          onClose={
            closeHolidayEditor
          }
          onChanged={
            handleHolidayChanged
          }
        />
      )}
    </section>
  );
}